import { Status, type StatusGroup, type StatusLayer } from '@status/shared';
import { Timeline } from './Timeline';
import {
  formatLatency,
  formatRelativeTime,
  formatUptime,
  slotHasData,
  statusVisual,
} from '../lib/format';

function StatusBadge({
  status,
  hasData,
}: {
  status: StatusLayer['current_status']['status'];
  hasData: boolean;
}): React.JSX.Element {
  const v = statusVisual(status, hasData);
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${v.badge}`}
    >
      <span className={`size-1.5 rounded-full ${v.dot}`} aria-hidden="true" />
      {v.label}
    </span>
  );
}

function LayerRow({ layer }: { layer: StatusLayer }): React.JSX.Element {
  const hasData = layer.current_status.timestamp > 0;
  // 周期内有没有样本决定 uptime 显示 0% 还是 —。
  // 只看 current_status 不够：模型可能刚被加进配置，有当前状态但周期内无历史。
  const hasHistory = layer.timeline.some(slotHasData);
  // 可用率数字与状态徽章共用同一份配色，两者永远一致
  const visual = statusVisual(layer.current_status.status, hasData && hasHistory);

  return (
    <div className="grid gap-2 border-t border-slate-200 py-3 first:border-t-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] sm:gap-4 dark:border-slate-800">
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2">
          <code className="truncate font-medium">{layer.model}</code>
          <StatusBadge status={layer.current_status.status} hasData={hasData} />
        </div>
        {/* 配置里没起别名时 model 就等于 request_model，重复显示两遍没意义 */}
        {layer.model !== layer.request_model && (
          <code className="block truncate text-xs text-slate-500 dark:text-slate-400">
            {layer.request_model}
          </code>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-slate-500 dark:text-slate-400">
          <span>
            可用率{' '}
            <span className={`font-medium tabular-nums ${visual.text}`}>
              {formatUptime(layer.uptime, hasHistory)}
            </span>
          </span>
          <span>
            延迟{' '}
            <span className="font-medium tabular-nums">
              {formatLatency(layer.current_status.latency)}
            </span>
          </span>
          {layer.current_status.status !== Status.AVAILABLE &&
            layer.current_status.http_code !== null && (
              <span className="tabular-nums">HTTP {layer.current_status.http_code}</span>
            )}
          <span className="tabular-nums">
            {formatRelativeTime(layer.current_status.timestamp)}
          </span>
        </div>
      </div>
      <Timeline slots={layer.timeline} label={layer.model} />
    </div>
  );
}

export function ProviderCard({ group }: { group: StatusGroup }): React.JSX.Element {
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-2">
        {/* 渠道标题不挂状态徽章：组状态取组内最差值，一个模型降级就会让
            整个渠道显示降级，反而盖住了「其它模型其实是好的」。逐个模型看更准。 */}
        <h2 className="text-base font-semibold">{group.provider}</h2>
        {/* 探测间隔不在这里重复：所有渠道目前用同一个间隔，每张卡片写一遍纯属噪音，
            页脚已经统一说明。真要给某个渠道单独设 interval_ms 时再考虑标出差异。 */}
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {group.layers.length} 个模型
        </span>
      </header>
      <div>
        {group.layers.map((layer) => (
          <LayerRow key={layer.request_model} layer={layer} />
        ))}
      </div>
    </section>
  );
}
